
import { TeamLibraryConst } from '@/constants/teamlibrary'
import helpers from '@/modules/library/helpers'
import { FileWithMetadata } from '@/modules/library/types'
import { Namespace } from '@/store/types'
import { ValidationRules } from '@/utils/input-validation'
import IconBin from '@svg/IconBin.svg'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import IconUploadCloud from '@svg/IconUploadCloud.svg'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { Getter } from 'vuex-class'

/**
 * DragDrop Component
 *
 * A Vue component that provides 3d model upload functionality through drag-and-drop or file selection.
 * Supports validation of file types and sizes, displays validation status, and manages file metadata.
 *
 * Features:
 * - Drag-and-drop file upload with visual feedback
 * - Click-to-select file input with optional multiple file selection
 * - File validation (type, size) with configurable constraints
 * - Visual indication of valid/invalid files with status messages
 * - File removal capability
 * - Responsive design with mobile optimizations
 *
 * Props:
 * - multiple: boolean - Enable/disable multiple file selection (default: true)
 * - accept: string[] - Array of accepted file types/extensions (e.g., ['.glb', '.usdz'])
 * - isUploading: boolean - Flag indicating upload in progress (default: false)
 *
 * Events:
 * - files-selected: Emitted when files are selected, removed, or cleared with the current file list
 *
 * Methods:
 * - clearFiles(): Clears all selected files and resets the file input
 */
@Component({
  components: {
    IconBin,
    IconCheckCircleFilled,
    IconCloseFilled,
    IconUploadCloud,
  },
})
export default class DragDrop extends Vue {
  @Prop({ type: Boolean, default: true }) readonly multiple!: boolean
  @Prop({ type: Boolean, default: true }) readonly fileSizeCheck!: boolean
  @Prop({ type: Boolean, default: false }) readonly fileDimensionCheck!: boolean
  @Prop({ type: Array, default: [] }) readonly accept!: string[]
  @Prop({ type: Boolean, default: false }) readonly isUploading!: boolean
  @Prop({ type: String, default: '' }) readonly customInstruction!: string

  @Getter('modelUploadMaxSizeMB', { namespace: Namespace.App })
  public modelUploadMaxSizeMB!: number

  public $refs!: Vue['$refs'] & {
    fileInput: HTMLInputElement | undefined
    listItems: HTMLElement[] | undefined
  }

  private isDragging: boolean = false
  private dragDepth: number = 0
  private files: File[] = []
  private filesList: FileWithMetadata[] = []
  private calculatedMaxHeight: string = 'auto'

  get maxFileSize(): number {
    return this.modelUploadMaxSizeMB * TeamLibraryConst.MaxFileSizeUnit.mb
  }

  get fileInput(): HTMLInputElement {
    return this.$refs.fileInput as HTMLInputElement
  }

  get selectedFilesSummary(): string {
    const fileSizeSum: number = this.files.reduce((partialSum: number, file: File) => partialSum + file.size, 0)

    const fileSizeString: string = this.formatFileSize(fileSizeSum)

    return `${this.files.length} file${this.files.length === 1 ? '' : 's'} (${fileSizeString} in total)`
  }

  get allowedModelFormatsInfo(): string {
    return this.accept.join(' / ').toUpperCase()
  }

  get instructionCopy(): string {
    if (this.customInstruction) return this.customInstruction

    let insCopy: string = this.allowedModelFormatsInfo

    if (this.fileSizeCheck) insCopy = `${this.maxFileSizeString} max. per file<br />${insCopy}`

    return insCopy
  }

  get maxFileSizeString(): string {
    return this.formatFileSize(this.maxFileSize)
  }

  private async updateListMaxHeight(): Promise<void> {
    await this.$nextTick()

    if (this.$refs.listItems && Array.isArray(this.$refs.listItems) && this.$refs.listItems.length > 0) {
      const firstItem = this.$refs.listItems[0] as HTMLElement
      const itemHeight = firstItem.offsetHeight
      this.calculatedMaxHeight = `${itemHeight * 3.5 + 8}px` // 3 + half <li> tags + 8px margin gap
    }
  }

  triggerFileInput(): void {
    if (this.fileInput == null) return

    this.fileInput.click()
  }

  onDragEnter(event: DragEvent): void {
    this.dragDepth += 1
    this.isDragging = true
  }

  onDragLeave(event: DragEvent): void {
    this.dragDepth -= 1
    // Only when we've fully left the container AND all its children
    if (this.dragDepth <= 0) {
      this.dragDepth = 0
      this.isDragging = false
    }
  }

  onDrop(event: DragEvent): void {
    // Handle your drop here
    this.dragDepth = 0
    this.isDragging = false

    if (!event.dataTransfer || !event.dataTransfer.files) return

    const droppedFiles = event.dataTransfer.files
    this.handleFiles(Array.from(droppedFiles))
  }

  onFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement
    if (target.files) {
      this.handleFiles(Array.from(target.files))
    }
  }

  async handleFiles(newFiles: File[]): Promise<void> {
    // Filter out files that are already selected
    const existingFileNames = new Set(this.files.map((file) => file.name))
    const filteredFiles = newFiles.filter((file) => !existingFileNames.has(file.name))

    const validatedFiles: FileWithMetadata[] = await this.validateFiles(filteredFiles)

    if (this.multiple) {
      this.filesList.push(...validatedFiles)
    } else {
      this.filesList = validatedFiles.slice(0, 1)
    }

    this.updateListMaxHeight()
    this.$emit('files-selected', this.files)
  }

  async validateFiles(files: File[]): Promise<FileWithMetadata[]> {
    const validatedFiles = await Promise.all(
      files.map(async (file: File) => {
        const fileExtensionString = file.name.split('.').pop()
        const fileExtension = fileExtensionString ? `.${fileExtensionString.toLowerCase()}` : ''
        const isAccepted = this.accept.some(
          (type) => type === fileExtension || (type.includes('*') && file.type.startsWith(type.replace('*', '')))
        )

        let validationStatus: TeamLibraryConst.FileStatus = TeamLibraryConst.FileStatus.FileValid
        let isValid: boolean = true
        let customErrorMsg: string = ''

        if (!isAccepted) {
          validationStatus = TeamLibraryConst.FileStatus.FileTypeError
          isValid = false
        } else if (this.fileSizeCheck && this.maxFileSize > 0 && file.size > this.maxFileSize) {
          validationStatus = TeamLibraryConst.FileStatus.FileSizeError
          isValid = false
        } else if (this.fileDimensionCheck && file.type.startsWith('image/')) {
          const result = await ValidationRules.ImageSizeLibraryThumbnail(file)

          if (!result.valid) {
            validationStatus = TeamLibraryConst.FileStatus.FileDimensionError
            isValid = false
            customErrorMsg = `${this.fileStatusMessage(validationStatus)}. Supplied dimension ${result.x} x ${result.y}.`
          }
        }

        if (isValid && this.multiple) {
          this.files.push(file)
        } else if (isValid) {
          this.files = [file]
        }

        return {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isValid,
          validationStatus,
          customErrorMsg,
        } as FileWithMetadata
      })
    )

    return validatedFiles
  }

  removeFile(fileName: string): void {
    this.files = this.files.filter((file: File) => file.name !== fileName)

    if (this.files.length === 0) {
      this.filesList = []
    } else {
      this.filesList = this.filesList.filter((fileItem: FileWithMetadata) => fileItem.name !== fileName)
      this.updateListMaxHeight()
    }
    this.$emit('on-files-deleted', this.filesList)
  }

  fileStatusMessage(validationStatus: TeamLibraryConst.FileStatus, customErrorMsg?: string): string {
    if (customErrorMsg) return customErrorMsg

    return TeamLibraryConst.FileStatusMessage[validationStatus]
  }

  formatFileSize(bytes: number): string {
    return helpers.formatFileSize(bytes)
  }

  clearFiles(): void {
    this.files = []
    this.filesList = []

    if (this.fileInput) {
      this.fileInput.value = ''
    }

    this.$emit('files-selected', this.files)
  }
}
